if (!customElements.get('slideshow-video-controls')) {
  class SlideshowVideoControls extends HTMLElement {
    connectedCallback() {
      this.media = this.closest('.slideshow__media');
      this.videos = Array.from(this.media.querySelectorAll('video'));
      if (!this.videos.length) return;

      this.soundButton = this.querySelector('[data-video-sound]');
      this.playButton = this.querySelector('[data-video-play]');
      this.soundLabel = this.soundButton && this.soundButton.querySelector('.slideshow__video-button-label');
      this.mobileQuery = window.matchMedia('(max-width: 749px)');

      this.soundOn = false;
      this.inView = true;
      this.ready = false;
      // Respect reduced motion: start paused, the shopper can still press play
      this.userPaused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (this.soundButton) this.soundButton.addEventListener('click', this.toggleSound.bind(this));
      if (this.playButton) this.playButton.addEventListener('click', this.togglePlay.bind(this));
      this.mobileQuery.addEventListener('change', this.update.bind(this));

      this.observer = new IntersectionObserver((entries) => {
        this.inView = entries[0].isIntersecting;
        this.update();
      }, { threshold: 0.25 });
      this.observer.observe(this.media);

      // Videos use preload="none": hold the download until the page has loaded so it doesn't
      // compete with the rest of the page, but don't wait longer than a few seconds
      const start = () => {
        if (this.ready) return;
        this.ready = true;
        this.update();
      };
      if (document.readyState === 'complete') {
        start();
      } else {
        window.addEventListener('load', start, { once: true });
        setTimeout(start, 3000);
      }
      this.render();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    get activeVideo() {
      if (this.videos.length === 1) return this.videos[0];
      const className = this.mobileQuery.matches ? 'slideshow__video--mobile' : 'slideshow__video--desktop';
      return this.videos.find((video) => video.classList.contains(className)) || this.videos[0];
    }

    toggleSound() {
      this.soundOn = !this.soundOn;
      if (this.soundOn) {
        // Turning sound on restarts the clip so the shopper hears it from the beginning
        this.activeVideo.currentTime = 0;
        this.userPaused = false;
      }
      this.update();
    }

    togglePlay() {
      this.userPaused = !this.userPaused;
      this.update();
    }

    update() {
      if (!this.ready) return;
      const active = this.activeVideo;
      this.videos.forEach((video) => {
        if (video === active) return;
        video.muted = true;
        video.pause();
      });

      active.muted = !this.soundOn;
      if (this.inView && !this.userPaused) {
        active.play().catch(() => {
          // Browser refused unmuted playback: fall back to muted so the video keeps playing
          if (!active.muted) {
            this.soundOn = false;
            active.muted = true;
            active.play().catch(() => {});
            this.render();
          }
        });
      } else {
        active.pause();
      }

      this.render();
    }

    render() {
      const playing = !this.userPaused;
      this.classList.toggle('is-sound-on', this.soundOn);
      this.classList.toggle('is-paused', !playing);
      if (!this.soundButton || !this.playButton) return;

      const soundText = this.soundOn ? this.dataset.labelMute : this.dataset.labelUnmute;
      this.soundButton.setAttribute('aria-pressed', this.soundOn);
      this.soundLabel.textContent = soundText;

      this.playButton.setAttribute('aria-label', playing ? this.dataset.labelPause : this.dataset.labelPlay);
    }
  }

  customElements.define('slideshow-video-controls', SlideshowVideoControls);
}
